import { useState, useCallback } from 'react';
import type { Special, SpecialSchedule } from '../types';
import {
    fetchSpecialsFromDb, saveSpecialInDb, deleteSpecialFromDb,
    saveScheduleInDb, deleteSchedulesForSpecialInDb,
} from '../services/specialsService';
import { getErrorMessage } from '../utils/errors';

export function useSpecials() {
    const [specials,         setSpecials]         = useState<Special[]>([]);
    const [specialSchedules, setSpecialSchedules] = useState<SpecialSchedule[]>([]);
    const [loading,          setLoading]          = useState(false);
    const [error,            setError]            = useState<string | null>(null);

    const fetchSpecials = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { specials: s, schedules } = await fetchSpecialsFromDb();
            setSpecials(s);
            setSpecialSchedules(schedules);
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch specials'));
        } finally {
            setLoading(false);
        }
    }, []);

    const saveSpecial = useCallback(async (
        payload: Parameters<typeof saveSpecialInDb>[0],
        editingId?: string
    ) => {
        return await saveSpecialInDb(payload, editingId);
    }, []);

    const deleteSpecial = useCallback(async (id: string) => {
        await deleteSpecialFromDb(id);
        setSpecials(prev => prev.filter(s => s.id !== id));
    }, []);

    const saveSchedule = useCallback(async (
        payload: object, specialId: string
    ) => {
        await saveScheduleInDb(payload, specialId, specialSchedules);
    }, [specialSchedules]);

    const deleteSchedules = useCallback(async (specialId: string) => {
        await deleteSchedulesForSpecialInDb(specialId);
    }, []);

    return {
        specials,
        specialSchedules,
        loading,
        error,
        fetchSpecials,
        saveSpecial,
        deleteSpecial,
        saveSchedule,
        deleteSchedules,
    };
}